import './style.scss'
import { Button, Checkbox, Drawer, Input, message, Modal, notification, Popconfirm, Radio, Space, Spin } from 'antd'
import dayjs from 'dayjs'
import { FC, Suspense, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cdnApi } from '@/api'
import * as homeApi from '@/api/home'
import error1 from '@/assets/error1.png'
import error2 from '@/assets/error2.png'
import error3 from '@/assets/error3.png'
import error4 from '@/assets/error4.png'
import { Play } from '@/assets/svg'
import SystemDeclare from '@/components/Declare'
import UploadVideo from '@/components/UploadVideo'
import VideoModal from '@/components/VideoModal'
import { UserStore } from '@/global-states'
import { demoVideos, eventTracking, showDeclare, showPlanModal, urlSource } from '@/libs/util'
import { DeleteOutlined, ExclamationCircleFilled } from '@ant-design/icons'
import AvatarFormModal from '../FormModal'

interface IProps {
  open: boolean
  onCancel?: () => void
  onOk?: () => void
  group?: any
}

const CreateAvatarDrawer: FC<IProps> = (props) => {
  const { open, group, onCancel, onOk } = props
  const { declareChecked } = UserStore
  const { t } = useTranslation(['system', 'avatar'])
  const [videoUrl, setVideoUrl] = useState<string>(undefined as any)
  const [videoInfo, setVideoInfo] = useState<any>({})
  const [step, setStep] = useState(0)
  const cancelTokenSource = useRef<any>()
  const [title, setTitle] = useState('')
  const [guideModalOpen, setGuideModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState<any>()
  const [cloneVoice, setCloneVoice] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [warrantOsskey, setWarrantOsskey] = useState('')
  const [checkedWarrant, setCheckedWarrant] = useState<any>()
  const [cloneMode, setCloneMode] = useState<any>(2)
  const [warrantVideos, setWarrantVideos] = useState<any[]>([])
  const [formModalOpen, setFormModalOpen] = useState(false)
  const [face, setFace] = useState<any>()

  useEffect(() => {
    setGuideModalOpen(!localStorage.getItem('guideRead'))
  }, [])

  useEffect(() => {
    if (open) {
      restartUpload()
      getOpenConfig()
    }
  }, [open])

  useEffect(() => {
    if (group?.id) {
      setTitle(`${group.title}-${t('avatar.video_material')}${(group.member_count || 0) + 1}`)
    }
  }, [group])

  useEffect(() => {
    if (cloneMode === 1) {
      getHistoryWarrantVideos()
    }
  }, [cloneMode])

  const getOpenConfig = async () => {
    const res = await cdnApi.get(`hiflys/create-avatar/config.json?v=${Date.now()}`)
    if (res.open === true) {
      setCreateOpen(true)
    }
  }

  const getHistoryWarrantVideos = async () => {
    const res = await homeApi.getHistoryWarrantVideos({
      page: 0,
      size: 100
    })
    setWarrantVideos(res?.list || [])
  }

  const onGuideModalClose = () => {
    setGuideModalOpen(false)
    localStorage.setItem('guideRead', '1')
  }

  const restartUpload = () => {
    if (videoInfo?.recommend) {
      UserStore.declareChecked = false
    }
    cancelUpload()
    setVideoUrl('')
    setWarrantOsskey('')
    setCheckedWarrant(undefined)
    setVideoInfo(undefined)
  }

  const cancelUpload = () => {
    if (cancelTokenSource) {
      cancelTokenSource.current?.cancel(t('system.cancel'))
    }
  }

  const selectDemoVideo = (v: any) => {
    UserStore.declareChecked = true
    setVideoUrl(urlSource(v.url))
    setStep(2)
    setVideoInfo({
      oss_key: v.ossKey,
      recommend: true
    })
  }

  const submitForm = async () => {
    if (!videoInfo?.oss_key) {
      return message.warning(t('avatar.upload_video_first'))
    }

    if (cloneMode === 1 && createOpen && !videoUrl) {
      if (!(warrantOsskey || checkedWarrant?.id)) {
        return message.warning(t('avatar.upload_warrant_video_first'))
      }
    }

    if (!declareChecked) {
      await showDeclare()
    }

    if (group?.id) {
      completeCreate()
    } else {
      setLoading(true)
      try {
        const res = await homeApi.faceExtraction({
          oss_key: videoInfo.oss_key
        })
        if (res?.extracted_face) {
          onCancel?.()
          setFormModalOpen(true)
          setFace(res.extracted_face)
        }
      } finally {
        setLoading(false)
      }
    }
  }

  const completeCreate = async (form?: any) => {
    eventTracking('InstantCloneConfirm')
    setLoading(true)
    try {
      const params: any = {
        oss_key: videoInfo?.oss_key,
        source_type: 1,
        also_clone_voice: cloneVoice,
        clone_mode: createOpen && !videoUrl ? cloneMode : undefined
      }

      if (checkedWarrant?.id) {
        params.warrant_video_id = checkedWarrant.id
      } else {
        params.warrant_voice_oss_key = warrantOsskey
      }

      if (group?.id) {
        params.title = title
        params.with_group = {
          group_id: group.id
        }
      } else {
        params.with_new_group = {
          ...(form || {})
        }
      }

      await homeApi.addDigitalHumans(params)
      if (createOpen && cloneMode === 1 && !videoUrl) {
        notification.success({
          message: t('avatar.under_review'),
          description: t('avatar.review_description')
        })
      } else {
        notification.success({
          message: t('avatar.clone_success'),
          description: t('avatar.clone_success_desc')
        })
      }
      onCancel?.()
      onOk?.()
      setFormModalOpen(false)
    } catch (error: any) {
      if (error.code === 1014) {
        eventTracking('AvatarCloneLimit')
        Modal.confirm({
          title: t('avatar.limit_reached'),
          content: <div>{t('avatar.limit_content')}</div>,
          okText: t('avatar.upgrade_membership'),
          cancelText: t('system.cancel'),
          onOk: () => {
            showPlanModal()
          }
        })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Suspense>
      <Drawer
        className="create-drawer create-avatar-drawer"
        open={open}
        title={group?.id ? t('avatar.add_video') : t('avatar.video_generated_avatar')}
        width={720}
        onClose={onCancel}
        footer={
          <>
            <SystemDeclare />
            <Space>
              <div className="fee">
                <div className="score">
                  <label className="light">{t('avatar.free_trial')}</label>
                </div>
              </div>
              <Button onClick={onCancel}>{t('system.cancel')}</Button>
              <Button type="primary" loading={loading} onClick={submitForm}>
                {loading ? t('avatar.submitting') : t('system.confirm')}
              </Button>
            </Space>
          </>
        }
      >
        <Spin spinning={loading} tip={group?.id ? t('avatar.submitting') : t('avatar.cloning')}>
          <div className="main">
            {group?.id && (
              <div className="form-item">
                <div className="title">{group?.id ? t('avatar.material_name') : t('avatar.avatar_name')}</div>
                <Input
                  maxLength={80}
                  placeholder={group?.id ? t('avatar.input_material_name') : t('avatar.input_avatar_name')}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
            )}

            <div className="form-item">
              <div className="title">
                {t('avatar.upload_video')}
                <ExclamationCircleFilled
                  onClick={() => {
                    eventTracking('InstantCloneTips')
                    setGuideModalOpen(true)
                  }}
                />
              </div>
              {step === 0 && (
                <>
                  <div className="warning">
                    <h4>{t('avatar.video_requirements')}</h4>
                    <div className="desc">
                      <div>
                        <label className="label">{t('avatar.video_orientation')}</label>
                        <label>{t('avatar.horizontal_or_vertical')}</label>
                      </div>
                      <div>
                        <label className="label">{t('avatar.file_format')}</label>
                        <label>{t('avatar.mp4_mov')}</label>
                      </div>
                      <div>
                        <label className="label">{t('avatar.video_duration')}</label>
                        <label>{t('avatar.5s_to_30min')}</label>
                      </div>
                      <div>
                        <label className="label">{t('avatar.resolution')}</label>
                        <label>{t('avatar.360p_to_4k')}</label>
                      </div>
                      <div>
                        <label className="label">{t('avatar.file_size')}</label>
                        <label>{t('avatar.less_than_500mb')}</label>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {!videoUrl && open && (
                <div className="content">
                  <UploadVideo
                    uploadContent={
                      <>
                        <p className="ant-upload-text">
                          {t('avatar.upload_video_prompt')}
                          {!group?.id && <label>{t('avatar.as_base_video')}</label>}
                        </p>
                        <p className="ant-upload-hint">{t('avatar.drag_or_click')}</p>
                      </>
                    }
                    onUploadSuccess={(video: any) => {
                      setVideoInfo({
                        oss_key: video.oss_key
                      })
                    }}
                    upateStep={(s) => setStep(s)}
                    onRestartUpload={restartUpload}
                    format={t('avatar.mp4_mov')}
                    size={500}
                    duration={{ min: 5, max: 1800, desc: t('avatar.5s_to_30min') }}
                    dpi={{ min: 360, max: 4096, desc: t('avatar.360p_to_4k') }}
                  />
                </div>
              )}

              {videoInfo?.oss_key && (
                <div className="step-view">
                  {videoUrl && (
                    <div className="step-view-box video">
                      <video controls src={videoUrl} />
                      <div className="trash" onClick={restartUpload}>
                        <DeleteOutlined />
                      </div>
                    </div>
                  )}
                  <div className="clone-voice">
                    <Checkbox
                      checked={cloneVoice}
                      onChange={(e) => {
                        setCloneVoice(e.target.checked)
                      }}
                    >
                      {t('avatar.clone_voice')}
                      <label className="gray">{t('avatar.voice_quality_warning')}</label>
                    </Checkbox>
                  </div>
                </div>
              )}

              {videoInfo?.oss_key && createOpen && !videoUrl && (
                <div className="form-item clone-mode">
                  <div className="title">
                    {t('avatar.select_clone_mode')}
                    <Popconfirm
                      title=""
                      description={
                        <div className="popconfirm-list" style={{ width: 620 }}>
                          <div>
                            <strong>{t('avatar.exact_clone')}</strong>
                            {t('avatar.exact_clone_desc')}
                          </div>
                          <div>
                            <strong>{t('avatar.similar_clone')}</strong>
                            {t('avatar.similar_clone_desc')}
                          </div>
                        </div>
                      }
                      okText={t('system.i_know')}
                      showCancel={false}
                      icon={null}
                    >
                      <ExclamationCircleFilled />
                    </Popconfirm>
                  </div>
                  <Radio.Group onChange={(e) => setCloneMode(e.target.value)} value={cloneMode}>
                    <Radio value={2}>{t('avatar.similar_clone')}</Radio>
                    <Radio value={1}>{t('avatar.exact_clone')}</Radio>
                  </Radio.Group>

                  {cloneMode === 1 && (
                    <>
                      {checkedWarrant?.id ? (
                        <div className="step-view-box video">
                          <video controls src={checkedWarrant.url} />
                          <div
                            className="trash"
                            onClick={() => {
                              setWarrantOsskey('')
                              setCheckedWarrant(undefined)
                            }}
                          >
                            <DeleteOutlined />
                          </div>
                        </div>
                      ) : (
                        <div className="content">
                          <UploadVideo
                            uploadContent={
                              <>
                                <p className="ant-upload-text">{t('avatar.record_warrant_video')}</p>
                                <p className="ant-upload-hint" style={{ fontSize: '13px' }}>
                                  {t('avatar.warrant_text')}
                                </p>
                              </>
                            }
                            onUploadSuccess={(video: any) => {
                              setWarrantOsskey(video.oss_key)
                            }}
                            onRestartUpload={() => {
                              setWarrantOsskey('')
                              setCheckedWarrant(undefined)
                            }}
                            format={t('avatar.video_formats')}
                            size={500}
                          />
                        </div>
                      )}

                      {!!warrantVideos?.length && (
                        <div className="history-video">
                          <div className="title">{t('avatar.use_previous_warrant')}</div>

                          <div className="warrant-list">
                            {warrantVideos.map((v: any) => (
                              <div className="warrant-list__item" key={v.id}>
                                <div className="box">
                                  <img src={urlSource(v.url, 'video')} />
                                  <div className="select">
                                    <Button
                                      type="primary"
                                      className="btn"
                                      onClick={() => {
                                        setCheckedWarrant(v)
                                      }}
                                    >
                                      {t('system.select')}
                                    </Button>
                                  </div>
                                </div>
                                <div className="time">{dayjs(v.create_time * 1000).format('YYYY-MM-DD HH:mm')}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {step === 0 && !group?.id && (
                <div className="video-demo">
                  <div className="title">
                    {t('avatar.demo_videos')}
                    <span className="yellow">{t('avatar.no_video_prompt')}</span>
                  </div>
                  <div className="demo-list">
                    {demoVideos.map((v) => (
                      <div className="list-item" key={v.url}>
                        <div
                          className="video"
                          onClick={() => setPreview({ url: urlSource(v.url), title: t('avatar.demo_video') })}
                        >
                          <div
                            className="bg"
                            style={{
                              backgroundImage: `url(${urlSource(v.url, 'video')})`
                            }}
                          ></div>
                          <img src={urlSource(v.url, 'video')} />
                          <div className="play">
                            <Play />
                          </div>
                        </div>
                        <div className="bottom">
                          <Button
                            type="primary"
                            onClick={() => {
                              selectDemoVideo(v)
                            }}
                          >
                            {t('system.select')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Modal
              title={t('avatar.video_requirements')}
              open={guideModalOpen}
              footer={
                <Button type="primary" onClick={onGuideModalClose}>
                  {t('system.i_know')}
                </Button>
              }
              closeIcon={false}
              width={580}
              onCancel={onGuideModalClose}
            >
              <div className="modal-guide">
                <ul>
                  <li>
                    <label>1.</label>
                    {t('avatar.guide_point1')}
                  </li>
                  <li>
                    <label>2.</label>
                    {t('avatar.guide_point2')}
                  </li>
                  <li>
                    <label>3.</label>
                    {t('avatar.guide_point3')}
                  </li>
                  <li>
                    <label>4.</label>
                    {t('avatar.guide_point4')}
                  </li>
                  <li>
                    <label>5.</label>
                    {t('avatar.guide_point5')}
                  </li>
                  <li>
                    <label>6.</label>
                    {t('avatar.guide_point6')}
                  </li>
                </ul>
                <div className="guide-error">
                  <div className="title-error">{t('avatar.error_examples')}</div>
                  <div className="error-list">
                    <div className="item">
                      <img className="img1" src={error1} />
                      <div>{t('avatar.multiple_faces')}</div>
                    </div>
                    <div className="item">
                      <img className="img2" src={error2} />
                      <div>{t('avatar.face_too_large')}</div>
                    </div>
                    <div className="item">
                      <img className="img3" src={error3} />
                      <div>{t('avatar.face_obstructed')}</div>
                    </div>
                    <div className="item">
                      <img className="img3" src={error4} />
                      <div>{t('avatar.no_face_detected')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </Modal>
          </div>
        </Spin>

        <VideoModal preview={preview} onCancel={() => setPreview(undefined)} />

        <AvatarFormModal
          open={formModalOpen}
          onCancel={() => setFormModalOpen(false)}
          onSuccess={completeCreate}
          avatar={face}
        />
      </Drawer>
    </Suspense>
  )
}

export default CreateAvatarDrawer
